import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

// Renders the embed markup exactly as it was inserted. A sandboxed iframe keeps
// third party scripts from touching the CMS while still letting them run, so
// the editor shows the same thing the site will.
const Embed = ({ html, attributes, isFocused }) => {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(240);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resize = () => {
      const body = iframe.contentDocument && iframe.contentDocument.body;
      if (!body) return;
      const next = Math.max(body.scrollHeight, 60);
      setHeight((current) => (current === next ? current : next));
    };

    iframe.addEventListener("load", resize);
    const interval = window.setInterval(resize, 1000);

    return () => {
      iframe.removeEventListener("load", resize);
      window.clearInterval(interval);
    };
  }, [html]);

  const wrapperStyle = {
    position: "relative",
    outline: isFocused ? "2px solid blue" : "none",
  };

  // Lets a click select the void block instead of hitting the embed itself.
  const maskStyle = {
    display: isFocused ? "none" : "block",
    position: "absolute",
    top: "0",
    left: "0",
    height: "100%",
    width: "100%",
    cursor: "cell",
    zIndex: 1,
  };

  const iframeStyle = {
    display: "block",
    width: "100%",
    height: `${height}px`,
    border: "none",
  };

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:sans-serif;}</style></head><body>${html || ""}</body></html>`;

  return (
    <div {...attributes}>
      <div style={wrapperStyle}>
        <div style={maskStyle} />
        <iframe
          ref={iframeRef}
          title="Embed"
          srcDoc={srcDoc}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
          allow="autoplay; fullscreen"
          allowFullScreen
          style={iframeStyle}
        />
      </div>
    </div>
  );
};

Embed.propTypes = {
  html: PropTypes.string,
  attributes: PropTypes.object.isRequired,
  isFocused: PropTypes.bool.isRequired,
};

export default Embed;
